// MatchConnection: the client talks to a room host over a WebSocket (online) or a MessagePort to a
// Web Worker running the same RoomCore (offline). Everything above this layer is identical. docs/07 §6.4.
import type { ClientMessage, RoomSettings, ServerMessage } from '@bcm/protocol';

export type ConnStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface Connection {
  readonly kind: 'online' | 'offline';
  send(msg: ClientMessage): void;
  close(): void;
}

export interface ConnectionHandlers {
  onMessage(msg: ServerMessage): void;
  onStatus(status: ConnStatus): void;
  /** Called on every (re)open; send `hello` here. */
  onOpen(): void;
}

export class WsConnection implements Connection {
  readonly kind = 'online' as const;
  private ws: WebSocket | null = null;
  private closedByUs = false;
  private attempt = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private url: string, private h: ConnectionHandlers) {
    this.open();
  }

  private open(): void {
    this.h.onStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.addEventListener('open', () => {
      this.attempt = 0;
      this.h.onStatus('open');
      this.h.onOpen();
      this.pingTimer = setInterval(() => this.send({ t: 'ping' }), 20_000);
    });
    ws.addEventListener('message', (ev) => {
      try {
        this.h.onMessage(JSON.parse(String(ev.data)) as ServerMessage);
      } catch (err) {
        console.error('bad server message', err);
      }
    });
    ws.addEventListener('close', () => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      if (this.closedByUs) return this.h.onStatus('closed');
      const delay = Math.min(8000, 400 * 2 ** this.attempt++);
      this.h.onStatus('reconnecting');
      setTimeout(() => !this.closedByUs && this.open(), delay);
    });
  }

  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  close(): void {
    this.closedByUs = true;
    this.ws?.close();
  }
}

/** Messages exchanged with the offline worker. */
export type WorkerIn = { kind: 'init'; settings: Partial<RoomSettings>; resume: boolean } | { kind: 'msg'; data: string };
/** `resumed`: the worker picked up the saved offline match instead of opening a new room. */
export type WorkerOut = { kind: 'msg'; data: ServerMessage } | { kind: 'ready'; resumed: boolean };

export class WorkerConnection implements Connection {
  readonly kind = 'offline' as const;
  private worker: Worker;

  /** Whether the worker resumed the saved match (known once it is open). */
  resumed = false;

  /** `resume`: go back to the saved offline match if there is one (else a new room opens). */
  constructor(settings: Partial<RoomSettings>, private h: ConnectionHandlers, resume = false) {
    this.worker = new Worker(new URL('./offline.worker.ts', import.meta.url), { type: 'module' });
    this.worker.addEventListener('message', (ev: MessageEvent<WorkerOut>) => {
      if (ev.data.kind === 'ready') {
        this.resumed = ev.data.resumed;
        this.h.onStatus('open');
        this.h.onOpen();
      } else this.h.onMessage(ev.data.data);
    });
    this.h.onStatus('connecting');
    this.worker.postMessage({ kind: 'init', settings, resume } satisfies WorkerIn);
  }

  send(msg: ClientMessage): void {
    this.worker.postMessage({ kind: 'msg', data: JSON.stringify(msg) } satisfies WorkerIn);
  }

  close(): void {
    this.worker.terminate();
    this.h.onStatus('closed');
  }
}

export function onlineUrl(code: string): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/api/rooms/${code}/ws`;
}
